/**
 * The blocks the verdicts placed today, as one section that opens like the
 * timeline's (user, 19/10/2026): closed, how many and on what; open, one row
 * per block — the gateway or lane, where it is blocked (every container or
 * one), the container that judged it, why, since when (Danish first) and on
 * how many sends. Open by default when there is any.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { Ban } from 'lucide-react';
import type { DropGenGatewayBlockRow } from '@trafficpulse/shared';

import { instantText } from '../../../utils/clock/dk-label';
import { ContainerChip } from '../../shared/ContainerChip';
import { useCountryClock } from '../../shared/time/country-clock-context';
import { Collapse } from '../../timeline/section/Collapse';
import { SectionToggle } from '../../timeline/section/SectionToggle';

const GRID = 'grid grid-cols-[minmax(170px,1.2fr)_120px_minmax(150px,1fr)_minmax(200px,1.6fr)_150px_90px] items-center gap-3 px-5';

const name = (b: DropGenGatewayBlockRow) => `${b.routeName ?? `Gateway ${b.routeId}`}${b.laneLabel ? ` · ${b.laneLabel}` : ''}`;

const summary = (blocks: DropGenGatewayBlockRow[]) => {
  const country = blocks.filter((b) => b.scope === 'COUNTRY');
  const names = [...new Set(blocks.map((b) => b.routeName ?? `#${b.routeId}`))];
  return [country.length > 0 ? `${country.length} country-wide` : null, names.slice(0, 3).join(', ') + (names.length > 3 ? ` +${names.length - 3}` : '')].filter(Boolean).join(' · ');
};

export const BlocksSection: FC<{ blocks: DropGenGatewayBlockRow[] }> = ({ blocks }) => {
  const [open, setOpen] = useState(true);
  const clock = useCountryClock();
  if (blocks.length === 0) return null;
  return (
    <div className="border-t border-border/40" data-testid="blocks-section">
      <SectionToggle icon={Ban} title="Blocked today" badge={blocks.length} summary={summary(blocks)} open={open} onToggle={() => setOpen((v) => !v)} />
      <Collapse open={open}>
        <div className="overflow-x-auto pb-2">
          <div className="min-w-[900px]">
            <div className={`${GRID} py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground`}>
              <span>Gateway</span><span>Blocked in</span><span>Judged by</span><span>Why</span><span>Since</span><span className="text-right">On</span>
            </div>
            {blocks.map((b) => (
              <div key={`${b.containerId}-${b.routeId}-${b.laneId}`} className={`${GRID} border-t border-border/30 py-1.5 text-xs`} data-testid="block-row">
                <span className="truncate font-medium" title={name(b)}>{name(b)}</span>
                <span className={b.scope === 'COUNTRY' ? 'font-semibold text-destructive' : 'text-muted-foreground'}>{b.scope === 'COUNTRY' ? 'every container' : 'this container'}</span>
                <span><ContainerChip name={b.containerName} color={b.containerColor} className="!px-2 !text-[10.5px]" /></span>
                <span className="truncate text-muted-foreground" title={b.reason}>{b.reason}</span>
                <span className="font-mono text-[11px] tabular-nums text-muted-foreground">{instantText(b.since, clock)}</span>
                <span className="text-right font-mono text-[11px] tabular-nums text-muted-foreground">{b.smsSent !== null ? `${b.smsSent.toLocaleString('en-US')} SMS` : '—'}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="px-5 pb-3 text-[11px] text-muted-foreground">A block lasts the day: tomorrow the gateway is back in the pool. Blocks come from the gateway verdict (container editor › Verdicts).</p>
      </Collapse>
    </div>
  );
};
